import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  IconButton,
  InputAdornment,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'next-i18next';
import type { FC } from 'react';
import React, { useState } from 'react';

import ScrollContainerWrapper from 'components/DeviceConfigComponents/ScrollContainerWrapper';
import CustomSwitchItem from 'components/MuiCustom/CustomSwitchItem';
import FilledTextField from 'components/MuiCustom/FilledTextField';

interface SetupTechnicianPasswordProps {
  technicianPassword: string;
  protectGridRequirement: boolean;
  protectDigitalInput: boolean;
  protectLoadbalancing: boolean;
  setTechnicianPassword: (value: string) => void;
  setProtectGridRequirement: (value: boolean) => void;
  setProtectDigitalInput: (value: boolean) => void;
  setProtectLoadbalancing: (value: boolean) => void;
  handleSubmitTechnicianPassword: (
    event: React.FormEvent<HTMLFormElement>,
  ) => void;
}

const SetupTechnicianPassword: FC<SetupTechnicianPasswordProps> = props => {
  const {
    technicianPassword,
    protectGridRequirement,
    protectDigitalInput,
    protectLoadbalancing,
    setTechnicianPassword,
    setProtectGridRequirement,
    setProtectDigitalInput,
    setProtectLoadbalancing,
    handleSubmitTechnicianPassword,
  } = props;

  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);

  return (
    <ScrollContainerWrapper height="60vh">
      <Box mb={4}>
        <Typography align="center" variant="h2" sx={{ mb: 2 }}>
          {t('common:pages.device_setup.technician_password_setup.title')}
        </Typography>
        <Typography align="center" variant="body1">
          {t('common:pages.device_setup.technician_password_setup.description')}
        </Typography>
      </Box>
      <Box height="100%" sx={{ overflowY: 'auto' }}>
        <form onSubmit={handleSubmitTechnicianPassword}>
          <Box mb={1} mt={2}>
            <FilledTextField
              label={t(
                'common:pages.settings.subpages.password.technician_password',
              )}
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={technicianPassword}
              onChange={e => setTechnicianPassword(e.target.value)}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <VisibilityOffIcon />
                      ) : (
                        <VisibilityIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </form>
        <Typography variant="body2" color="grey.600" pl={1}>
          {t(
            'common:pages.device_setup.technician_password_setup.password_validation',
          )}
        </Typography>
        <Typography variant="body1" fontWeight={600} mt={4} pl={1}>
          {t(
            'common:pages.devices.device_list.group_config.protected_sections.title',
          )}
        </Typography>
        <CustomSwitchItem mt={2} isFirstItem>
          <Typography align="center" variant="body1">
            {t(
              'common:pages.devices.device_list.group_config.protected_sections.country_grid',
            )}
          </Typography>
          <Switch
            checked={protectGridRequirement}
            onChange={() => setProtectGridRequirement(!protectGridRequirement)}
          />
        </CustomSwitchItem>
        <CustomSwitchItem>
          <Typography align="center" variant="body1">
            {t(
              'common:pages.devices.device_list.group_config.sections.digital_input',
            )}
          </Typography>
          <Switch
            checked={protectDigitalInput}
            onChange={() => setProtectDigitalInput(!protectDigitalInput)}
          />
        </CustomSwitchItem>
        <CustomSwitchItem mb={2} isLastItem>
          <Typography align="center" variant="body1">
            {t(
              'common:pages.devices.device_list.group_config.sections.load_balancing',
            )}
          </Typography>
          <Switch
            checked={protectLoadbalancing}
            onChange={() => setProtectLoadbalancing(!protectLoadbalancing)}
          />
        </CustomSwitchItem>
      </Box>
    </ScrollContainerWrapper>
  );
};

export default SetupTechnicianPassword;
